import { FC, useEffect, useState } from "react";
import { Alert, Skeleton, Stack, TextField, styled } from "@mui/material";
import { PatientDetailsWithId } from "../../types";
import * as apiClient from "../../api";
import PatientCard from "./PatientCard";

const LoadingCard = styled(Skeleton)({ height: "3rem" });

interface ClinicianDetailsTabProps {
  clinicianId: string;
}

const ClinicianPatientsTab: FC<ClinicianDetailsTabProps> = ({
  clinicianId,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [sharedPatients, setSharedPatients] = useState<PatientDetailsWithId[]>(
    [],
  );
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadSharedPatients = async () => {
      const res = await apiClient.getSharedPatients(clinicianId);
      setSharedPatients(res.data.patients);
      setLoaded(true);
    };
    loadSharedPatients();
  }, [clinicianId]);

  const handleRemove = (patientId: string) => {
    apiClient.unsharePatientClinician(patientId, clinicianId);
    const updatedSharedPatients = sharedPatients.filter(
      (sharedPatient) => sharedPatient.patientId !== patientId,
    );
    setSharedPatients(updatedSharedPatients);
    setShowSaved(true);
  };

  const handleCloseSavedAlert = () => {
    setShowSaved(false);
  };

  const bySearch = ({ firstName, lastName, email }: PatientDetailsWithId) => {
    const lowerSearch = search.toLowerCase();
    return search
      ? firstName.toLowerCase().includes(lowerSearch) ||
          lastName.toLowerCase().includes(lowerSearch) ||
          email.toLowerCase().includes(lowerSearch) ||
          `${firstName} ${lastName}`.toLowerCase().includes(lowerSearch)
      : true;
  };

  const filteredSharedPatients = sharedPatients.filter(bySearch);

  return (
    <Stack spacing={2}>
      {showSaved && (
        <Alert severity="success" onClose={handleCloseSavedAlert}>
          Successfully removed patient
        </Alert>
      )}
      <TextField
        id="shared-patients-search"
        label="Search"
        variant="outlined"
        type="search"
        autoComplete="off"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {!loaded ? (
        <>
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
        </>
      ) : (
        filteredSharedPatients.map((sharedPatient) => (
          <PatientCard
            key={sharedPatient.patientId}
            patient={sharedPatient}
            handleRemove={handleRemove}
          />
        ))
      )}
    </Stack>
  );
};

export default ClinicianPatientsTab;
